/**
 * Card compacto de produto - Layout horizontal tipo lista
 */

import React, { useCallback, useState, useEffect } from 'react';
import {
  Card,
  Box,
  Typography,
  Chip,
  IconButton
} from '@mui/material';
import { ShoppingCart, Inventory, Store } from '@mui/icons-material';
import { Produto, ProdutoPrevisaoVendas } from '@types';
import { FormatUtil, SessionUtil } from '@utils';
import { LazyAvatar } from '@components/common';
import { ControladorQuantidade } from './ControladorQuantidade';
import { CampoPrecoEditavel } from './CampoPrecoEditavel';
import { ModalPrevisaoVendas } from './ModalPrevisaoVendas';
import { produtoService } from '@services';
import theme from '@theme';

interface ProdutoCardItemProps {
  produto: Produto;
  tipo?: 'visualizacao' | 'previsao' | 'pedido';
  quantidade?: number;
  precoEditado?: number;
  disponivel?: number;
  estoque?: number;
  usandoEstoqueLoja?: boolean;
  onAddToCart?: () => void;
  onUpdateQuantidade?: (quantidade: number) => void;
  onUpdatePreco?: (preco: number) => void;
  disabled?: boolean;
}

export const ProdutoCardItem: React.FC<ProdutoCardItemProps> = React.memo(({
  produto,
  tipo = 'visualizacao',
  quantidade = 0,
  precoEditado,
  disponivel,
  estoque,
  usandoEstoqueLoja = false,
  onAddToCart,
  onUpdateQuantidade,
  onUpdatePreco,
  disabled = false,
}) => {
  const [previsaoAtiva, setPrevisaoAtiva] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [previsoes, setPrevisoes] = useState<ProdutoPrevisaoVendas[]>([]);
  const [loadingPrevisoes, setLoadingPrevisoes] = useState(false);

  // Verifica se a previsão ainda está ativa
  useEffect(() => {
    if (tipo !== 'previsao') return;

    // Parse da data brasileira
    const parseDataBrasileira = (dataStr: string): Date => {
      if (!dataStr) return new Date();
      const partes = dataStr.split(' ');
      const [dia, mes, ano] = partes[0].split('/');
      const hora = partes[1] || '00:00:00';
      const [hh, mm, ss = '00'] = hora.split(':');
      return new Date(
        parseInt(ano),
        parseInt(mes) - 1,
        parseInt(dia),
        parseInt(hh),
        parseInt(mm),
        parseInt(ss)
      );
    };

    const verificarPrevisao = () => {
      const vendedor = SessionUtil.getVendedor();
      if (!vendedor || !vendedor.indPrevisaoFim) {
        setPrevisaoAtiva(false);
        return;
      }

      const agora = new Date();
      const dataFim = parseDataBrasileira(vendedor.indPrevisaoFim);
      const ativa = agora.getTime() <= dataFim.getTime();

      setPrevisaoAtiva(ativa);
    };

    // Verifica imediatamente
    verificarPrevisao();

    // Verifica a cada segundo
    const interval = setInterval(verificarPrevisao, 1000);

    return () => clearInterval(interval);
  }, [tipo]);

  const getImageUrl = (filename: string): string => {
    if (!filename) return '';
    return FormatUtil.formatImageUrl(`${filename}`);
  };

  const handleIncrement = useCallback(() => {
    if (onUpdateQuantidade) {
      const newQtd = quantidade + 1;

      // Para previsão, não há limite
      if (tipo === 'previsao') {
        onUpdateQuantidade(newQtd);
        return;
      }

      // Para pedido, valida contra estoque
      if (tipo === 'pedido') {
        if (!estoque || newQtd <= estoque) {
          onUpdateQuantidade(newQtd);
        }
        return;
      }

      // Para outros tipos, valida contra disponivel
      if (!disponivel || newQtd <= disponivel) {
        onUpdateQuantidade(newQtd);
      }
    }
  }, [onUpdateQuantidade, quantidade, tipo, disponivel, estoque]);

  const handleDecrement = useCallback(() => {
    if (onUpdateQuantidade) {
      const newQtd = Math.max(0, quantidade - 1);
      onUpdateQuantidade(newQtd);
    }
  }, [onUpdateQuantidade, quantidade]);

  const handleQuantidadeManual = useCallback((valor: number) => {
    if (onUpdateQuantidade) {
      onUpdateQuantidade(valor);
    }
  }, [onUpdateQuantidade]);

  const handleImageClick = useCallback(async () => {
    // Só abre modal no modo pedido
    if (tipo !== 'pedido') return;

    const vendedor = SessionUtil.getVendedor();
    if (!vendedor || !vendedor.indPrevisao) {
      console.warn('Vendedor ou previsão não encontrado');
      return;
    }

    setModalOpen(true);
    setLoadingPrevisoes(true);

    try {
      const data = await produtoService.obterPrevisaoVendas(
        vendedor.indPrevisao,
        produto.codigo
      );
      setPrevisoes(data);
    } catch (error) {
      console.error('Erro ao carregar previsões:', error);
      setPrevisoes([]);
    } finally {
      setLoadingPrevisoes(false);
    }
  }, [tipo, produto.codigo]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const precoAtual = precoEditado || parseFloat(produto.precoVenda);

  return (
    <Card
      sx={{
        mb: 1,
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 1,
          p: 1.5,
          minHeight: 80
        }}
      >
        {/* Coluna 1: Imagem */}
        <LazyAvatar
          sx={{
            width: 56,
            height: 56,
            bgcolor: 'grey.100',
            flexShrink: 0,
            backgroundColor : theme.palette.elite.contrastText,
            cursor: tipo === 'pedido' ? 'pointer' : 'default',
            '&:hover': tipo === 'pedido' ? {
              opacity: 0.8,
              transition: 'opacity 0.2s',
            } : {},
          }}
          src={getImageUrl(produto.imagem)}
          alt={produto.nome}
          variant="rounded"
          loading="lazy"
          onClick={handleImageClick}
        >
          <Inventory sx={{ color: 'grey.400' }} />
        </LazyAvatar>

        {/* Coluna 2: Nome + Chips (Inventory e Store) */}
        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
            {produto.nome}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={FormatUtil.formatUnit(produto.unidade, produto.peso)}
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />

            {tipo === 'pedido' && (
              <Chip
                size="small"
                icon={<Inventory sx={{ fontSize: '0.75rem' }} />}
                label={`${produto.estoque}`}
                color={Number(produto.estoque) > 0 ? 'success' : 'error'}
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}

            {tipo === 'pedido' && Number(produto.estoque_loja) > 0 && (
              <Chip
                size="small"
                icon={<Store sx={{ fontSize: '0.75rem' }} />}
                label={`${produto.estoque_loja}`}
                color={Number(produto.estoque) > 0 ? 'default' : (Number(produto.estoque_loja) > 0 ? 'success' : 'error')}
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Box>

          {/* Preço - Apenas para visualização (não pedido/previsão) */}
          {tipo !== 'previsao' && tipo !== 'pedido' && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="primary" fontWeight={600}>
                {FormatUtil.formatCurrency(precoAtual)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Coluna 3: Preço editável + Controlador (vertical) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center', alignItems: 'flex-end', flexShrink: 0 }}>
          {/* Preço editável para pedido com estoque */}
          {tipo === 'pedido' && onUpdatePreco && estoque !== undefined && Number(estoque) > 0 && (
            <CampoPrecoEditavel
              valor={precoAtual}
              valorMinimo={parseFloat(produto.precoMin)}
              onChange={onUpdatePreco}
              disabled={disabled}
            />
          )}

          {tipo === 'visualizacao' && onAddToCart && (
            <IconButton
              color="primary"
              onClick={onAddToCart}
              disabled={disabled || (disponivel !== undefined && disponivel === 0)}
              size="small"
            >
              <ShoppingCart fontSize="small" />
            </IconButton>
          )}

          {tipo === 'previsao' && onUpdateQuantidade && previsaoAtiva && (
            <ControladorQuantidade
              quantidade={quantidade}
              onIncrement={handleIncrement}
              onDecrement={handleDecrement}
              disabled={disabled}
              size="small"
              editavel
              onChange={(valor) => onUpdateQuantidade(valor)}
            />
          )}

          {tipo === 'pedido' && onUpdateQuantidade && (Number(estoque) > 0 || Number(produto.estoque_loja) > 0) && (
            <ControladorQuantidade
              quantidade={quantidade}
              onIncrement={handleIncrement}
              onDecrement={handleDecrement}
              max={Number(estoque) > 0 ? Number(estoque) : Number(produto.estoque_loja)}
              disabled={disabled || (Number(estoque) === 0 && Number(produto.estoque_loja) === 0)}
              usandoEstoqueLoja={usandoEstoqueLoja}
              size="small"
              editavel
              onChange={handleQuantidadeManual}
            />
          )}
        </Box>
      </Box>

      {/* Modal de Previsões de Vendas */}
      <ModalPrevisaoVendas
        open={modalOpen}
        onClose={handleCloseModal}
        produtoCodigo={produto.codigo}
        produtoNome={produto.nome}
        previsoes={previsoes}
        isLoading={loadingPrevisoes}
      />
    </Card>
  );
});

ProdutoCardItem.displayName = 'ProdutoCardItem';
